import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button, Slider, Select, Typography, Spin, Input } from 'antd';
import apiClient from '../../../api/client';

const { Text } = Typography;

// Character sets ordered from dark to light
const CHAR_SETS = {
    standard: ' .:-=+*#%@',
    blocks: ' ░▒▓█',
    dots: ' ·•●◉⬤',
    minimal: ' .:█',
    braille: ' ⠁⠃⠇⡇⣇⣧⣷⣿',
    detailed: ' .\'`^",:;Il!i><~+_-?][}{1)(|\\/tfjrxnuvczXYUJCLQ0OZmwqpdbkhao*#MW&8%B@$',
};

/**
 * AsciiEditorPanel — Convert image to ASCII/text art.
 * Renders characters on canvas based on luminance values.
 */
const AsciiEditorPanel = ({ visible, onClose, onApply, sourceImageUrl, proxyUrl, canvasId }) => {
    const previewCanvasRef = useRef(null);
    const sourceImgRef = useRef(null);
    const processingRef = useRef(false);
    const pendingRef = useRef(false);

    const [charSet, setCharSet] = useState('standard');
    const [columns, setColumns] = useState(80);
    const [fontSize, setFontSize] = useState(10);
    const [lineHeight, setLineHeight] = useState(1.0);
    const [bgColor, setBgColor] = useState('#0d1117');
    const [textColor, setTextColor] = useState('#00ff41');
    const [colorMode, setColorMode] = useState('mono'); // mono, original, tinted
    const [contrast, setContrast] = useState(1.2);
    const [brightness, setBrightness] = useState(0);
    const [invert, setInvert] = useState(false);

    // User palettes
    const [userPalettes, setUserPalettes] = useState([]);
    useEffect(() => {
        if (!visible) return;
        const fetchPalettes = async () => {
            try {
                const response = await apiClient.get('/color-palettes', { params: canvasId ? { canvasId } : {} });
                setUserPalettes(response.data || []);
            } catch (e) { /* silent */ }
        };
        fetchPalettes();
    }, [visible, canvasId]);

    const applyAsciiPalette = (palette) => {
        const colors = (palette.colors || []).map(c => typeof c === 'string' ? c : c.color).filter(Boolean);
        if (colors.length >= 1) setTextColor(colors[0]);
        if (colors.length >= 2) setBgColor(colors[1]);
    };

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!visible || !sourceImageUrl) return;
        setLoading(true);
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => { sourceImgRef.current = img; setLoading(false); };
        img.onerror = () => setLoading(false);
        img.src = proxyUrl || sourceImageUrl;
    }, [visible, sourceImageUrl, proxyUrl]);

    useEffect(() => {
        if (!visible || !sourceImgRef.current) return;
        renderAscii();
    }, [visible, charSet, columns, fontSize, lineHeight, bgColor, textColor, colorMode, contrast, brightness, invert, loading]);

    const renderAscii = useCallback(() => {
        if (!sourceImgRef.current || !previewCanvasRef.current) return;
        if (processingRef.current) { pendingRef.current = true; return; }
        processingRef.current = true;
        requestAnimationFrame(() => {
            const img = sourceImgRef.current;
            const maxDim = 800;
            const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
            const pw = Math.round(img.width * scale);
            const ph = Math.round(img.height * scale);
            try { doRender(previewCanvasRef.current, pw, ph); }
            finally {
                processingRef.current = false;
                if (pendingRef.current) { pendingRef.current = false; renderAscii(); }
            }
        });
    }, [charSet, columns, fontSize, lineHeight, bgColor, textColor, colorMode, contrast, brightness, invert]);

    const doRender = (canvas, targetW, targetH) => {
        const img = sourceImgRef.current;
        if (!img || !canvas) return;

        // Use provided target dimensions or fall back to original image size
        const outW = targetW || img.width;
        const outH = targetH || img.height;

        const chars = CHAR_SETS[charSet] || CHAR_SETS.standard;
        const lh = lineHeight;
        const charAspect = 0.55; // monospace chars are taller than wide

        // Compute font size so columns fit exactly into output width
        const cols = columns;
        const fs = outW / (cols * charAspect);
        const charW = fs * charAspect; // = outW / cols
        const charH = fs * lh;
        const rows = Math.max(1, Math.round(outH / charH));

        // Sample image at grid resolution
        const sampleCanvas = document.createElement('canvas');
        sampleCanvas.width = cols;
        sampleCanvas.height = rows;
        const sampleCtx = sampleCanvas.getContext('2d');
        sampleCtx.drawImage(img, 0, 0, cols, rows);
        const sampleData = sampleCtx.getImageData(0, 0, cols, rows).data;

        // Alpha data
        const alphaCanvas = document.createElement('canvas');
        alphaCanvas.width = cols;
        alphaCanvas.height = rows;
        const alphaCtx = alphaCanvas.getContext('2d');
        alphaCtx.drawImage(img, 0, 0, cols, rows);
        const alphaData = alphaCtx.getImageData(0, 0, cols, rows).data;

        // Output canvas matches source image dimensions
        canvas.width = outW;
        canvas.height = outH;
        const ctx = canvas.getContext('2d');

        // Background
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, 0, outW, outH);

        ctx.font = `${fs}px "Courier New", "Consolas", monospace`;
        ctx.textBaseline = 'top';

        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
                const idx = (row * cols + col) * 4;
                const r = sampleData[idx];
                const g = sampleData[idx + 1];
                const b = sampleData[idx + 2];
                const a = alphaData[idx + 3];

                if (a < 10) continue; // Skip transparent pixels

                // Pre-filter
                let pr = r / 255, pg = g / 255, pb = b / 255;
                pr += brightness; pg += brightness; pb += brightness;
                pr = (pr - 0.5) * contrast + 0.5;
                pg = (pg - 0.5) * contrast + 0.5;
                pb = (pb - 0.5) * contrast + 0.5;
                pr = Math.max(0, Math.min(1, pr));
                pg = Math.max(0, Math.min(1, pg));
                pb = Math.max(0, Math.min(1, pb));

                let lum = 0.299 * pr + 0.587 * pg + 0.114 * pb;
                if (invert) lum = 1 - lum;

                // Map luminance to character
                const charIdx = Math.round(lum * (chars.length - 1));
                const ch = chars[charIdx];
                if (ch === ' ') continue;

                // Color
                if (colorMode === 'original') {
                    ctx.fillStyle = `rgba(${Math.round(pr * 255)}, ${Math.round(pg * 255)}, ${Math.round(pb * 255)}, ${a / 255})`;
                } else if (colorMode === 'tinted') {
                    const tc = hexToRgb(textColor);
                    const mix = 0.6;
                    const mr = Math.round(tc[0] * mix + pr * 255 * (1 - mix));
                    const mg = Math.round(tc[1] * mix + pg * 255 * (1 - mix));
                    const mb = Math.round(tc[2] * mix + pb * 255 * (1 - mix));
                    ctx.fillStyle = `rgba(${mr}, ${mg}, ${mb}, ${a / 255})`;
                } else {
                    // Mono
                    const tc = hexToRgb(textColor);
                    ctx.fillStyle = `rgba(${tc[0]}, ${tc[1]}, ${tc[2]}, ${(a / 255) * lum})`;
                }

                ctx.fillText(ch, col * charW, row * charH);
            }
        }
    };

    const hexToRgb = (hex) => {
        const h = hex.replace('#', '');
        return [parseInt(h.substring(0, 2), 16) || 0, parseInt(h.substring(2, 4), 16) || 0, parseInt(h.substring(4, 6), 16) || 0];
    };

    const handleApply = useCallback(() => {
        const img = sourceImgRef.current;
        if (!img) return;
        // Render at full original image resolution
        const fullCanvas = document.createElement('canvas');
        doRender(fullCanvas, img.width, img.height);
        onApply?.(fullCanvas.toDataURL('image/png'));
    }, [charSet, columns, fontSize, lineHeight, bgColor, textColor, colorMode, contrast, brightness, invert, onApply]);

    const sliderRow = (label, min, max, value, onChange, step = 1) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Text style={{ color: '#8b949e', fontSize: 11, minWidth: 80, whiteSpace: 'nowrap' }}>{label}</Text>
            <Slider min={min} max={max} step={step} value={value} onChange={onChange}
                style={{ flex: 1, margin: 0 }}
                styles={{ track: { background: '#10b981' }, rail: { background: '#30363d' }, handle: { borderColor: '#10b981', width: 12, height: 12 } }}
            />
            <Text style={{ color: '#10b981', fontSize: 11, fontFamily: 'monospace', minWidth: 36, textAlign: 'right' }}>
                {step < 1 ? value.toFixed(2) : value}
            </Text>
        </div>
    );

    const colorRow = (label, value, onChange) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <Text style={{ color: '#8b949e', fontSize: 11, minWidth: 80 }}>{label}</Text>
            <div
                style={{ width: 24, height: 24, borderRadius: 4, background: value, border: '1px solid #30363d', cursor: 'pointer' }}
                onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'color'; input.value = value;
                    input.style.cssText = 'position:absolute;opacity:0;pointer-events:none';
                    document.body.appendChild(input);
                    input.addEventListener('input', (e) => onChange(e.target.value));
                    input.addEventListener('change', () => document.body.removeChild(input));
                    input.click();
                }}
            />
            <Text style={{ color: '#6e7681', fontSize: 10, fontFamily: 'monospace' }}>{value}</Text>
        </div>
    );

    if (!visible) return null;

    return (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 110, display: 'flex', background: 'rgba(13, 17, 23, 0.95)', pointerEvents: 'auto' }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, overflow: 'hidden' }}>
                {loading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                        <Spin size="large" /><Text style={{ color: '#8b949e' }}>Loading image...</Text>
                    </div>
                ) : (
                    <canvas ref={previewCanvasRef} style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8, boxShadow: '0 8px 40px rgba(0,0,0,0.6)', border: '1px solid #30363d' }} />
                )}
            </div>

            <div style={{ width: 310, flexShrink: 0, borderLeft: '1px solid #30363d', display: 'flex', flexDirection: 'column', background: '#0d1117' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid #30363d', background: '#161b22', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 14, fontFamily: 'monospace' }}>A_</span>
                        <span style={{ color: '#c9d1d9', fontWeight: 600, fontSize: 14 }}>ASCII Art</span>
                    </div>
                    <Button type="text" size="small" onClick={onClose} style={{ color: '#8b949e', fontSize: 12, padding: '2px 6px' }}>✕</Button>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div>
                        <Text style={{ color: '#10b981', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Character Set</Text>
                        <Select value={charSet} onChange={setCharSet} style={{ width: '100%', marginTop: 6 }}
                            options={[
                                { value: 'standard', label: 'Standard  .:-=+*#%@' },
                                { value: 'blocks', label: 'Blocks  ░▒▓█' },
                                { value: 'dots', label: 'Dots  ·•●◉' },
                                { value: 'minimal', label: 'Minimal  .:█' },
                                { value: 'braille', label: 'Braille  ⠁⠃⠇⡇⣿' },
                                { value: 'detailed', label: 'Detailed (70 chars)' },
                            ]}
                        />
                    </div>

                    <div>
                        <Text style={{ color: '#10b981', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Layout</Text>
                        <div style={{ marginTop: 8 }}>
                            {sliderRow('Columns', 20, 200, columns, setColumns)}
                            {sliderRow('Font Size', 4, 24, fontSize, setFontSize)}
                            {sliderRow('Line Height', 0.5, 2.0, lineHeight, (v) => setLineHeight(v), 0.05)}
                        </div>
                    </div>

                    <div>
                        <Text style={{ color: '#10b981', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Color</Text>
                        <Select value={colorMode} onChange={setColorMode} style={{ width: '100%', marginTop: 6 }}
                            options={[
                                { value: 'mono', label: 'Monochrome' },
                                { value: 'original', label: 'Original Colors' },
                                { value: 'tinted', label: 'Tinted' },
                            ]}
                        />
                        <div style={{ marginTop: 8 }}>
                            {colorRow('Text Color', textColor, setTextColor)}
                            {colorRow('Background', bgColor, setBgColor)}
                        </div>

                        {/* User Palettes */}
                        {userPalettes.length > 0 && (
                            <div style={{ marginTop: 8 }}>
                                <Text style={{ color: '#6e7681', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>From Palette</Text>
                                <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    {userPalettes.map((palette) => {
                                        const colors = (palette.colors || []).map(c => typeof c === 'string' ? c : c.color).filter(Boolean);
                                        if (colors.length === 0) return null;
                                        return (
                                            <div
                                                key={palette.id || palette.name}
                                                onClick={() => applyAsciiPalette(palette)}
                                                style={{
                                                    cursor: 'pointer', padding: '4px 6px', borderRadius: 4,
                                                    border: '1px solid #30363d', background: '#161b22',
                                                    transition: 'all 0.15s ease',
                                                }}
                                                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#10b981'; e.currentTarget.style.background = '#1c2333'; }}
                                                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#30363d'; e.currentTarget.style.background = '#161b22'; }}
                                            >
                                                <Text style={{ color: '#c9d1d9', fontSize: 10, display: 'block', marginBottom: 3 }}>{palette.name}</Text>
                                                <div style={{ display: 'flex', gap: 2 }}>
                                                    {colors.map((c, i) => (
                                                        <div key={i} style={{ width: 14, height: 14, borderRadius: 2, background: c, border: '1px solid rgba(255,255,255,0.1)', flexShrink: 0 }} />
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    <div>
                        <Text style={{ color: '#10b981', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Image</Text>
                        <div style={{ marginTop: 8 }}>
                            {sliderRow('Contrast', 0.5, 3.0, contrast, (v) => setContrast(v), 0.01)}
                            {sliderRow('Brightness', -0.5, 0.5, brightness, (v) => setBrightness(v), 0.01)}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                <Text style={{ color: '#8b949e', fontSize: 11, minWidth: 80 }}>Invert</Text>
                                <Button size="small" type={invert ? 'primary' : 'text'}
                                    onClick={() => setInvert(!invert)}
                                    style={{ color: invert ? undefined : '#6e7681', fontSize: 10, background: invert ? '#10b981' : undefined, borderColor: invert ? '#10b981' : undefined }}>
                                    {invert ? 'ON' : 'OFF'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                <div style={{ padding: '12px 14px', borderTop: '1px solid #30363d', display: 'flex', gap: 8, flexShrink: 0, background: '#161b22' }}>
                    <Button onClick={onClose} style={{ flex: 1, background: '#21262d', borderColor: '#30363d', color: '#c9d1d9' }}>Cancel</Button>
                    <Button type="primary" onClick={handleApply} style={{ flex: 1, background: '#10b981', borderColor: '#10b981' }}>Apply</Button>
                </div>
            </div>
        </div>
    );
};

export default AsciiEditorPanel;
